/**
 * The evidence bank.
 *
 * Freshness is the column that does work here, so it is shown on every card
 * rather than hidden behind a filter: a bank that looks full and is half stale
 * produces refusals the customer cannot explain.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, errorMessage, type Evidence } from '../api';
import { Alert, Button, Dialog, EmptyState, Field, Input, PageHeader, Spinner, Textarea, formatDate } from '../ui';
import {
  BookOpenIcon,
  CheckIcon,
  ClockIcon,
  EyeIcon,
  EyeOffIcon,
  Icon,
  IconHover,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from '../icons';

interface Draft {
  id?: string;
  title: string;
  source: string;
  body: string;
  public: boolean;
}

const EMPTY: Draft = { title: '', source: '', body: '', public: false };

function FreshnessChip({ card }: { card: Evidence }) {
  if (card.stale) {
    return (
      <span className="chip chip-refusal">
        <Icon icon={TriangleAlertIcon} size={12} isAnimated={false} />
        Stale — not citable
      </span>
    );
  }
  if (card.daysUntilStale <= 30) {
    return (
      <span className="chip chip-warn">
        <Icon icon={ClockIcon} size={12} isAnimated={false} />
        Review in {card.daysUntilStale}d
      </span>
    );
  }
  return (
    <span className="chip chip-neutral">
      <Icon icon={CheckIcon} size={12} isAnimated={false} />
      Reviewed {formatDate(card.reviewedAt)}
    </span>
  );
}

export function EvidenceView() {
  const [cards, setCards] = useState<Evidence[] | null>(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { evidence } = await api.listEvidence();
      setCards(evidence);
    } catch (err) {
      setError(errorMessage(err));
      setCards([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!cards) return [];
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.source.toLowerCase().includes(q) ||
        c.body.toLowerCase().includes(q),
    );
  }, [cards, query]);

  const staleCount = cards?.filter((c) => c.stale).length ?? 0;

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      if (draft.id) {
        await api.updateEvidence(draft.id, {
          title: draft.title,
          source: draft.source,
          body: draft.body,
          public: draft.public,
        });
      } else {
        await api.createEvidence({
          title: draft.title,
          source: draft.source,
          body: draft.body,
          public: draft.public,
        });
      }
      setDraft(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const review = async (id: string) => {
    await api.reviewEvidence(id).catch(() => undefined);
    await load();
  };

  const remove = async (card: Evidence) => {
    if (!confirm(`Delete "${card.title}"? Answers citing it will need re-drafting.`)) return;
    await api.deleteEvidence(card.id).catch(() => undefined);
    await load();
  };

  const togglePublic = async (card: Evidence) => {
    await api.updateEvidence(card.id, { public: !card.public }).catch(() => undefined);
    await load();
  };

  return (
    <>
      <PageHeader
        title="Evidence"
        description="Every claim you can back up, with its source and the date a human last checked it. Answers may only cite what is in here."
        actions={
          <Button variant="primary" icon={PlusIcon} onClick={() => setDraft({ ...EMPTY })}>
            New card
          </Button>
        }
      />

      {error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {staleCount > 0 ? (
        <div className="mb-4">
          <Alert tone="warn">
            {staleCount === 1 ? '1 card has' : `${staleCount} cards have`} gone past their review
            date and cannot be cited until someone confirms they are still true.
          </Alert>
        </div>
      ) : null}

      {cards === null ? (
        <div className="flex justify-center py-16">
          <Spinner size={20} />
        </div>
      ) : cards.length === 0 ? (
        <EmptyState
          icon={BookOpenIcon}
          title="Nothing to cite yet"
          action={
            <Button variant="primary" icon={PlusIcon} onClick={() => setDraft({ ...EMPTY })}>
              Write the first card
            </Button>
          }
        >
          Start with the four questions every questionnaire asks: encryption at rest, encryption in
          transit, access control, and who your subprocessors are. One card each, with the policy or
          report it comes from.
        </EmptyState>
      ) : (
        <>
          <div className="relative mb-4">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint">
              <Icon icon={SearchIcon} size={15} isAnimated={false} />
            </span>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search evidence"
              className="pl-9"
            />
          </div>

          <div className="space-y-3">
            {filtered.map((card) => (
              <IconHover key={card.id} className="card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-[15px] font-medium">{card.title}</h3>
                    <p className="mt-0.5 text-[13px] text-muted">{card.source}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <FreshnessChip card={card} />
                  </div>
                </div>

                <p className="prose-body mt-3 text-[13.5px] text-muted">{card.body}</p>

                <div className="mt-4 flex flex-wrap items-center gap-1.5 border-t border-line pt-3">
                  <span className="citation chip chip-neutral">{card.id}</span>

                  <div className="ml-auto flex flex-wrap items-center gap-1">
                    <Button
                      small
                      variant="ghost"
                      icon={card.public ? EyeIcon : EyeOffIcon}
                      onClick={() => togglePublic(card)}
                    >
                      {card.public ? 'On trust page' : 'Private'}
                    </Button>
                    <Button small variant="ghost" icon={RefreshCwIcon} onClick={() => review(card.id)}>
                      Mark reviewed
                    </Button>
                    <Button
                      small
                      variant="ghost"
                      icon={PencilIcon}
                      onClick={() =>
                        setDraft({
                          id: card.id,
                          title: card.title,
                          source: card.source,
                          body: card.body,
                          public: card.public,
                        })
                      }
                    >
                      Edit
                    </Button>
                    <Button small variant="ghost" icon={Trash2Icon} onClick={() => remove(card)}>
                      Delete
                    </Button>
                  </div>
                </div>
              </IconHover>
            ))}

            {filtered.length === 0 ? (
              <p className="py-10 text-center text-[13.5px] text-faint">
                No evidence matches “{query}”.
              </p>
            ) : null}
          </div>
        </>
      )}

      {draft ? (
        <Dialog
          title={draft.id ? 'Edit evidence' : 'New evidence'}
          description="What you can prove, and where it comes from."
          onClose={() => setDraft(null)}
          footer={
            <>
              <Button onClick={() => setDraft(null)}>Cancel</Button>
              <Button
                variant="primary"
                icon={CheckIcon}
                busy={busy}
                disabled={!draft.title.trim() || !draft.source.trim() || !draft.body.trim()}
                onClick={save}
              >
                Save
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Field label="Title">
              <Input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Encryption at rest"
              />
            </Field>

            <Field
              label="Source"
              hint="The document this comes from. A reader should be able to go and check it."
            >
              <Input
                value={draft.source}
                onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                placeholder="Security policy §4, reviewed by K. Mensah"
              />
            </Field>

            <Field label="What it says">
              <Textarea
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                placeholder="All customer data is stored encrypted with AES-256. Keys are managed in AWS KMS with rotation every 90 days."
                rows={6}
              />
            </Field>

            <label className="flex cursor-pointer items-start gap-2.5 rounded-lg bg-sunken p-3">
              <input
                type="checkbox"
                checked={draft.public}
                onChange={(e) => setDraft({ ...draft, public: e.target.checked })}
                className="mt-0.5 accent-[var(--vk-accent)]"
              />
              <span className="text-[13px]">
                Show on the public trust page
                <span className="block text-faint">
                  Anyone with the link will read this exactly as written.
                </span>
              </span>
            </label>

            {draft.id ? (
              <p className="text-[12.5px] text-faint">
                Saving an edit does not reset the review date. Use “Mark reviewed” for that — the two
                are different claims.
              </p>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
